/**
 * borrowedPool.ts
 * ──────────────────────────────────────────────────────────────
 * Which subjects a student has agreed to practise with UEM questions.
 *
 * The founder settled that borrowing is opt-in per practice session and that
 * nothing is stored server side, so there is no Student column and no
 * migration. sessionStorage is the honest match for that decision: it is
 * per-tab, it survives navigating from the question page to the subject list
 * and back, and it is gone when the tab closes.
 *
 * It exists because the opt-in is given on QuestionPage while the offline
 * pack is filled from SubjectSelectPage. Without somewhere to put it, a
 * student could opt in, practise online, go offline and find nothing cached,
 * which is the one way this feature fails silently.
 *
 * **Keyed by student id, and cleared on logout.** The first version used one
 * global key. On a shared phone, student A opting in for Química meant
 * student B inherited that consent: the server re-checks that B's own pool is
 * empty, which it is for the same institution, but it cannot tell whether B
 * ever saw the prompt. Consent is the one thing this storage exists to carry,
 * so it has to belong to somebody. Mirrors invalidateSubjectsCache, which
 * already solves exactly this for the subjects cache.
 *
 * Every access is wrapped: sessionStorage throws in private mode in some
 * browsers, and losing an opt-in costs the student one extra tap, so a
 * failure here is always a no-op rather than an error.
 */
const KEY_PREFIX = 'tutor.borrowed_pool_subjects'

function keyFor(studentId: string): string {
  return `${KEY_PREFIX}.${studentId}`
}

/**
 * Reads this student's opt-ins.
 *
 * Fails closed on a missing id: no student means no proven consent, so the
 * caller borrows nothing rather than falling back to a shared bucket.
 */
function read(studentId: string | undefined | null): Set<string> {
  if (!studentId) return new Set()
  try {
    const raw = sessionStorage.getItem(keyFor(studentId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set(parsed.filter((c): c is string => typeof c === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

export function hasBorrowOptIn(studentId: string | undefined | null, subjectCode: string): boolean {
  return read(studentId).has(subjectCode.toUpperCase())
}

export function rememberBorrowOptIn(studentId: string | undefined | null, subjectCode: string): void {
  if (!studentId) return
  try {
    const set = read(studentId)
    set.add(subjectCode.toUpperCase())
    sessionStorage.setItem(keyFor(studentId), JSON.stringify([...set]))
  } catch { /* sessionStorage disabled: the opt-in just lasts one page */ }
}

/** The codes the offline pack request should ask to borrow for. */
export function borrowOptInSubjectCodes(studentId: string | undefined | null): string[] {
  return [...read(studentId)]
}

/**
 * Drop this student's opt-ins. Called from logout.
 *
 * Also sweeps any value left under the old global key, so a tab that opted in
 * before this change does not keep leaking consent to the next account that
 * signs in on it.
 */
export function clearBorrowOptIns(studentId: string | undefined | null): void {
  try {
    if (studentId) sessionStorage.removeItem(keyFor(studentId))
    sessionStorage.removeItem(KEY_PREFIX)
  } catch { /* nothing to do if storage is unavailable */ }
}
